// src/components/settings/AdminSettingsPage.jsx
//
// Superadmin Settings (/admin/settings). Business policy that used to need a
// developer and a .env edit. Secrets stay in .env: the Integrations tab only
// shows whether they're set.
//
// Edits collect into one draft across all tabs. "Review and save" lists every
// change (old -> new) and asks for the superadmin's own password; the server
// checks it again before writing. ?tab=<id> opens a tab directly.
//
// Settings are defined on the server (app_settings/registry.py): a new
// setting appears here without touching this file.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../../api/apiClient';
import FleetHeader from '../FleetHeader';
import { LoadingScreen } from '../billing/billingShared';
import ConfirmChangesSheet from './ConfirmChangesSheet';
import HistoryPanel from './HistoryPanel';
import IntegrationsPanel from './IntegrationsPanel';
import SettingField from './SettingField';
import { draftToValue, sameValue, valueToDraft } from './settingsShared';
import '../../styles/fleet-theme.css';
import '../billing/Billing.css';
import './Settings.css';

// Light checks so obvious mistakes are caught before the password step.
// The server validates everything again.
function quickCheck(s, value) {
  if (s.type === 'int' || s.type === 'float') {
    if (value === '' || Number.isNaN(value)) return 'Enter a number.';
    if (value < s.min || value > s.max) return `Must be between ${s.min} and ${s.max}.`;
  }
  if (s.type === 'multi_choice' && value.length < (s.min_items || 0)) return `Choose at least ${s.min_items}.`;
  if (s.type === 'email_list') {
    const bad = value.find((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) return `"${bad}" isn't an email address.`;
  }
  if (s.type === 'string' && s.max_length && String(value).length > s.max_length) return `Up to ${s.max_length} characters.`;
  return null;
}

export default function AdminSettingsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [drafts, setDrafts] = useState({});      // key -> raw input
  const [resets, setResets] = useState({});      // key -> true
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const tab = new URLSearchParams(location.search).get('tab') || 'support';
  const setTab = (id) => navigate(`/admin/settings?tab=${id}`, { replace: true });

  const load = useCallback(async () => {
    try {
      setData(await apiFetchJSON('/api/v2/admin/settings'));
      setLoadError(null);
    } catch (e) {
      setLoadError(e.status === 403 ? 'Only superadmins can open Settings.' : e.message || 'Could not load settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pending changes, in registry order.
  const changes = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const s of data.settings) {
      if (resets[s.key]) {
        out.push({ setting: s, reset: true, value: null });
      } else if (s.key in drafts) {
        const value = draftToValue(s, drafts[s.key]);
        if (!sameValue(value, s.value)) out.push({ setting: s, reset: false, value });
      }
    }
    return out;
  }, [data, drafts, resets]);

  const dirtyKeys = useMemo(() => new Set(changes.map((c) => c.setting.key)), [changes]);

  // Warn before closing the tab / reloading with unsaved changes.
  useEffect(() => {
    if (!changes.length) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [changes.length]);

  if (loadError) {
    return (
      <div>
        <FleetHeader title="Settings" user={user} onLogout={onLogout} scope="admin" />
        <div className="sd-page bl-page st-page"><div className="ft-toolbar-error" role="alert">{loadError}</div></div>
      </div>
    );
  }
  if (!data) return <LoadingScreen text="Loading settings…" />;

  const settingsTabs = new Set(data.settings.map((s) => s.tab));
  const current = data.tabs.find((t) => t.id === tab) || data.tabs[0];
  const tabSettings = data.settings.filter((s) => s.tab === current.id);
  const groups = [];
  tabSettings.forEach((s) => {
    const g = groups.find((x) => x.name === s.group);
    if (g) g.items.push(s); else groups.push({ name: s.group, items: [s] });
  });

  const draftFor = (s) => (s.key in drafts ? drafts[s.key] : valueToDraft(s, s.value));
  const change = (key, raw) => {
    setDrafts((d) => ({ ...d, [key]: raw }));
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };
  const reset = (key) => {
    setResets((r) => ({ ...r, [key]: true }));
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };
  const undoReset = (key) => setResets((r) => { const n = { ...r }; delete n[key]; return n; });
  const discard = () => { setDrafts({}); setResets({}); setErrors({}); };

  const review = () => {
    const errs = {};
    changes.forEach((c) => {
      if (!c.reset) {
        const msg = quickCheck(c.setting, c.value);
        if (msg) errs[c.setting.key] = msg;
      }
    });
    setErrors(errs);
    const first = changes.find((c) => errs[c.setting.key]);
    if (first) {
      setTab(first.setting.tab);
      return;
    }
    setConfirming(true);
  };

  const save = async (password) => {
    const payload = {};
    changes.forEach((c) => { payload[c.setting.key] = c.reset ? null : c.value; });
    try {
      const out = await apiFetchJSON('/api/v2/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({ changes: payload, password }),
      });
      setData({ tabs: out.tabs, settings: out.settings });
      discard();
      setConfirming(false);
      setHistoryKey((k) => k + 1);
      const n = out.applied.length;
      setToast(n ? `Saved ${n} ${n === 1 ? 'change' : 'changes'}. They apply within about 15 seconds.` : 'Nothing changed.');
    } catch (e) {
      if (e.data?.field_errors && Object.keys(e.data.field_errors).length) {
        setErrors(e.data.field_errors);
        setConfirming(false);
        const firstKey = Object.keys(e.data.field_errors)[0];
        const s = data.settings.find((x) => x.key === firstKey);
        if (s) setTab(s.tab);
        return;
      }
      throw e; // wrong password / locked: shown in the sheet
    }
  };

  return (
    <div>
      <FleetHeader title="Settings" user={user} onLogout={onLogout} scope="admin" />
      <div className={`sd-page bl-page st-page ${changes.length ? 'has-savebar' : ''}`}>
        <div className="st-intro">
          <h1 className="st-title">Settings</h1>
          <p className="st-subtitle">
            Superadmins only. Every save asks for your password and is recorded in History.
            Keys and passwords stay with your developer in the server’s .env.
          </p>
        </div>

        {toast && (
          <div className="bl-toast st-toast" role="status">
            {toast}
            <button type="button" className="bl-icon-btn" onClick={() => setToast(null)} aria-label="Dismiss"><i className="fas fa-times"></i></button>
          </div>
        )}

        <nav className="st-tabs" aria-label="Settings sections">
          {data.tabs.map((t) => {
            const pending = changes.filter((c) => c.setting.tab === t.id).length;
            return (
              <button key={t.id} type="button" className={`st-tab ${t.id === current.id ? 'is-active' : ''}`}
                      aria-current={t.id === current.id ? 'page' : undefined} onClick={() => setTab(t.id)}>
                <i className={`fas ${t.icon}`} aria-hidden="true"></i>
                <span>{t.label}</span>
                {pending > 0 && <span className="st-tab-count" aria-label={`${pending} unsaved`}>{pending}</span>}
              </button>
            );
          })}
        </nav>

        <div className="st-panel">
          {settingsTabs.has(current.id) && groups.map((g) => (
            <section key={g.name} className="st-group">
              <h2 className="st-group-title">{g.name}</h2>
              {g.items.map((s) => (
                <SettingField
                  key={s.key}
                  s={s}
                  draft={draftFor(s)}
                  error={errors[s.key]}
                  dirty={dirtyKeys.has(s.key)}
                  resetting={!!resets[s.key]}
                  onChange={(raw) => change(s.key, raw)}
                  onReset={() => reset(s.key)}
                  onUndoReset={() => undoReset(s.key)}
                />
              ))}
            </section>
          ))}
          {current.id === 'integrations' && <IntegrationsPanel />}
          {current.id === 'history' && <HistoryPanel settings={data.settings} refreshKey={historyKey} />}
        </div>
      </div>

      {changes.length > 0 && (
        <div className="st-savebar" role="region" aria-label="Unsaved changes">
          <span className="st-savebar-count">
            <span className="st-dirty-dot" aria-hidden="true" />
            {changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}
          </span>
          <div className="st-savebar-actions">
            <button type="button" className="bl-btn-secondary bl-btn-sm" onClick={discard}>Discard</button>
            <button type="button" className="bl-btn-primary bl-btn-sm" onClick={review}>Review and save</button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmChangesSheet changes={changes} tabs={data.tabs} onCancel={() => setConfirming(false)} onConfirm={save} />
      )}
    </div>
  );
}
